import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FiSmartphone,
  FiShoppingBag,
  FiTruck,
  FiStar,
  FiGlobe,
  FiMoon,
  FiTag,
  FiMonitor,
  FiTablet,
} from "react-icons/fi";
import { useLanguage } from "../context/LanguageContext";

const Home = () => {
  const { t } = useLanguage();

  return (
    <div>
      {/* Hero Section */}
      <section className="relative py-16 md:py-24 bg-gradient-to-br from-zinc-50 to-white overflow-hidden">
        {/* Background Texture */}
        <div
          className="absolute inset-0 z-0 opacity-[0.03]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        ></div>

        {/* Background Decorations */}
        <div className="absolute -top-20 -right-20 w-64 h-64 bg-orange-100 rounded-full opacity-20 z-0"></div>
        <div className="absolute bottom-10 left-10 w-32 h-32 bg-blue-100 rounded-full opacity-20 z-0"></div>

        <div className="container-custom relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center mb-16">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="text-center md:text-left"
            >
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
                <span className="gradient-text">
                  {t("heroTitle").split(" ")[0]}
                </span>{" "}
                {t("heroTitle").split(" ").slice(1).join(" ")}
              </h1>
              <p className="text-lg md:text-xl text-gray-700 mb-8">
                {t("heroDescription")}
              </p>
              <div className="flex flex-col sm:flex-row justify-center md:justify-start space-y-4 sm:space-y-0 sm:space-x-4">
                <Link to="/contact" className="btn btn-primary">
                  {t("getStarted")}
                </Link>
                <a
                  href="https://foodapp.solylock.dev"
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-secondary"
                >
                  {t("tryDemoButton")}
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="relative hidden md:block"
            >
              <div className="bg-gray-900 rounded-2xl p-4 shadow-xl relative mx-auto md:ml-auto md:mr-0 max-w-sm">
                <div className="rounded-xl overflow-hidden">
                  <img
                    src="https://i.imgur.com/NvGJKnF.png"
                    alt="ByteBelly Food App Screenshot"
                    className="w-full h-auto"
                  />
                </div>
                <div className="absolute -bottom-5 -right-5 bg-orange-500 text-white rounded-full p-4 shadow-lg">
                  <FiStar className="h-6 w-6" />
                </div>
              </div>
            </motion.div>
          </div>

          {/* App Preview Display - Original desktop design but improved mobile responsiveness */}
          <div className="flex flex-col md:flex-row justify-center items-center gap-12 mb-16">
            {/* Combined mockup display that adapts for mobile */}
            <div className="w-full relative h-auto md:h-[500px] lg:h-[600px]">
              {/* For mobile devices only - simple side by side mockups */}
              <div className="flex flex-row justify-center items-start gap-6 mb-12 md:hidden">
                {/* Admin Mobile App */}
                <div className="transform transition-all duration-500 hover:scale-105 w-[45%]">
                  <div className="relative bg-black rounded-[24px] border-[6px] border-gray-800 shadow-lg overflow-hidden aspect-[9/19]">
                    {/* Notch */}
                    <div className="absolute top-0 w-14 h-4 bg-black left-1/2 transform -translate-x-1/2 rounded-b-xl z-10"></div>

                    {/* Screen */}
                    <div className="h-full w-full pt-5 pb-3 flex items-center justify-center bg-gradient-to-b from-gray-900 to-black">
                      <img
                        src="https://i.imgur.com/QT60F5t.png"
                        alt="ByteBelly Admin App"
                        className="h-full object-cover object-center"
                        style={{ maxWidth: "95%" }}
                      />
                    </div>

                    {/* Home indicator */}
                    <div className="absolute bottom-1 w-10 h-1 bg-gray-500 left-1/2 transform -translate-x-1/2 rounded-full"></div>
                  </div>
                  <div className="mt-2 text-center">
                    <div className="bg-gradient-to-r from-blue-600 to-blue-500 text-white text-xs px-3 py-1 rounded-full inline-block shadow-md">
                      Admin
                    </div>
                  </div>
                </div>

                {/* Customer Mobile App */}
                <div className="transform transition-all duration-500 hover:scale-105 w-[45%]">
                  <div className="relative bg-black rounded-[24px] border-[6px] border-gray-800 shadow-lg overflow-hidden aspect-[9/19]">
                    {/* Notch */}
                    <div className="absolute top-0 w-14 h-4 bg-black left-1/2 transform -translate-x-1/2 rounded-b-xl z-10"></div>

                    {/* Screen */}
                    <div className="h-full w-full pt-5 pb-3 flex items-center justify-center bg-gradient-to-b from-gray-900 to-black">
                      <img
                        src="https://i.imgur.com/FsRbh4o.png"
                        alt="ByteBelly Customer App"
                        className="h-full object-cover object-center"
                        style={{ maxWidth: "95%" }}
                      />
                    </div>

                    {/* Home indicator */}
                    <div className="absolute bottom-1 w-10 h-1 bg-gray-500 left-1/2 transform -translate-x-1/2 rounded-full"></div>
                  </div>
                  <div className="mt-2 text-center">
                    <div className="bg-gradient-to-r from-orange-600 to-orange-500 text-white text-xs px-3 py-1 rounded-full inline-block shadow-md">
                      Customer
                    </div>
                  </div>
                </div>
              </div>

              {/* Original desktop design - visible only on MD+ screens */}
              <div className="hidden md:block relative h-full w-full">
                {/* Floating circles decorative elements */}
                <div className="absolute left-1/4 top-10 w-32 h-32 bg-blue-500 opacity-5 rounded-full blur-xl"></div>
                <div className="absolute right-1/3 bottom-10 w-40 h-40 bg-orange-500 opacity-5 rounded-full blur-xl"></div>
                <div className="absolute right-1/4 top-1/4 w-20 h-20 bg-green-500 opacity-5 rounded-full blur-xl"></div>

                {/* Desktop Dashboard - centered and elevated */}
                <div className="absolute left-1/2 -translate-x-1/2 bottom-8 w-[90%] sm:w-[80%] md:w-[70%] lg:w-[60%] mx-auto z-10 transform transition-all duration-500 hover:scale-105 hover:-translate-y-2">
                  <div className="relative mx-auto">
                    {/* Monitor Frame */}
                    <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-t-xl p-2 flex items-center justify-between shadow-lg">
                      <div className="flex space-x-1.5">
                        <div className="h-3 w-3 rounded-full bg-red-500"></div>
                        <div className="h-3 w-3 rounded-full bg-yellow-500"></div>
                        <div className="h-3 w-3 rounded-full bg-green-500"></div>
                      </div>
                      <div className="text-xs text-gray-400 font-medium">
                        Restaurant Dashboard
                      </div>
                      <div className="w-6"></div>
                    </div>
                    <div className="bg-gray-900 border-2 border-gray-800 p-1 relative overflow-hidden">
                      {/* Reflection overlay */}
                      <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent pointer-events-none z-10"></div>
                      <img
                        src="https://i.imgur.com/O80bYRH.png"
                        alt="ByteBelly Desktop Dashboard"
                        className="w-full h-auto relative z-0"
                      />
                    </div>
                    <div className="bg-gradient-to-r from-gray-900 to-gray-800 h-3 rounded-b-lg"></div>
                    <div className="bg-gradient-to-r from-gray-700 to-gray-800 h-10 w-32 mx-auto rounded-b-xl shadow-lg"></div>
                    {/* Monitor reflection - more subtle and natural */}
                    <div className="absolute -bottom-6 left-0 right-0 h-6 bg-black/10 blur-md rounded-full mx-16"></div>
                  </div>
                </div>

                {/* Admin Mobile App - left floating */}
                <div className="absolute left-[5%] sm:left-[2%] bottom-[35%] md:left-0 md:bottom-1/3 w-[160px] sm:w-[180px] md:w-[200px] transform rotate-[-12deg] hover:rotate-0 transition-all duration-500 z-30">
                  <div className="relative bg-black rounded-[38px] border-[8px] border-gray-800 shadow-2xl overflow-hidden h-[320px] sm:h-[360px] md:h-[400px]">
                    {/* Notch */}
                    <div className="absolute top-0 w-20 h-6 bg-black left-1/2 transform -translate-x-1/2 rounded-b-xl z-10"></div>

                    {/* Power button */}
                    <div className="absolute -right-[8px] top-20 w-[8px] h-16 bg-gray-700 rounded-l-md"></div>

                    {/* Volume buttons */}
                    <div className="absolute -left-[8px] top-20 w-[8px] h-10 bg-gray-700 rounded-r-md"></div>
                    <div className="absolute -left-[8px] top-36 w-[8px] h-10 bg-gray-700 rounded-r-md"></div>

                    {/* Screen */}
                    <div className="h-full w-full pt-8 pb-5 flex items-center justify-center bg-gradient-to-b from-gray-900 to-black relative">
                      <img
                        src="https://i.imgur.com/QT60F5t.png"
                        alt="ByteBelly Admin App"
                        className="h-full object-cover object-center"
                        style={{ maxWidth: "95%" }}
                      />
                      {/* Screen reflection */}
                      <div className="absolute inset-0 bg-gradient-to-tl from-transparent via-transparent to-white/10 pointer-events-none"></div>
                    </div>

                    {/* Home indicator */}
                    <div className="absolute bottom-2 w-16 h-1 bg-gray-500 left-1/2 transform -translate-x-1/2 rounded-full"></div>
                  </div>
                  <div className="absolute -bottom-4 w-full text-center">
                    <div className="bg-gradient-to-r from-blue-600 to-blue-500 text-white text-xs px-4 py-1.5 rounded-full inline-block shadow-lg">
                      Admin
                    </div>
                  </div>
                </div>

                {/* Customer Mobile App - right floating */}
                <div className="absolute right-[5%] sm:right-[2%] bottom-[35%] md:right-0 md:bottom-1/3 w-[160px] sm:w-[180px] md:w-[200px] transform rotate-12 hover:rotate-0 transition-all duration-500 z-30">
                  <div className="relative bg-black rounded-[38px] border-[8px] border-gray-800 shadow-2xl overflow-hidden h-[320px] sm:h-[360px] md:h-[400px]">
                    {/* Notch */}
                    <div className="absolute top-0 w-20 h-6 bg-black left-1/2 transform -translate-x-1/2 rounded-b-xl z-10"></div>

                    {/* Power button */}
                    <div className="absolute -right-[8px] top-20 w-[8px] h-16 bg-gray-700 rounded-l-md"></div>

                    {/* Volume buttons */}
                    <div className="absolute -left-[8px] top-20 w-[8px] h-10 bg-gray-700 rounded-r-md"></div>
                    <div className="absolute -left-[8px] top-36 w-[8px] h-10 bg-gray-700 rounded-r-md"></div>

                    {/* Screen */}
                    <div className="h-full w-full pt-8 pb-5 flex items-center justify-center bg-gradient-to-b from-gray-900 to-black relative">
                      <img
                        src="https://i.imgur.com/FsRbh4o.png"
                        alt="ByteBelly Customer App"
                        className="h-full object-cover object-center"
                        style={{ maxWidth: "95%" }}
                      />
                      {/* Screen reflection */}
                      <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-transparent to-white/10 pointer-events-none"></div>
                    </div>

                    {/* Home indicator */}
                    <div className="absolute bottom-2 w-16 h-1 bg-gray-500 left-1/2 transform -translate-x-1/2 rounded-full"></div>
                  </div>
                  <div className="absolute -bottom-4 w-full text-center">
                    <div className="bg-gradient-to-r from-orange-600 to-orange-500 text-white text-xs px-4 py-1.5 rounded-full inline-block shadow-lg">
                      Customer
                    </div>
                  </div>
                </div>

                {/* Floating connecting lines */}
                <svg
                  className="absolute inset-0 w-full h-full z-0 opacity-30"
                  viewBox="0 0 100 100"
                  preserveAspectRatio="none"
                >
                  <line
                    x1="20"
                    y1="60"
                    x2="50"
                    y2="80"
                    stroke="rgba(59, 130, 246, 0.2)"
                    strokeWidth="0.3"
                  />
                  <line
                    x1="80"
                    y1="60"
                    x2="50"
                    y2="80"
                    stroke="rgba(249, 115, 22, 0.2)"
                    strokeWidth="0.3"
                  />
                </svg>
              </div>

              {/* Desktop mockup for mobile devices - shown below the phones on mobile */}
              <div className="block md:hidden w-full max-w-md mx-auto transform transition-all duration-500 hover:scale-105">
                <div className="relative mx-auto">
                  {/* Monitor Frame */}
                  <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-t-xl p-2 flex items-center justify-between">
                    <div className="flex space-x-1.5">
                      <div className="h-3 w-3 rounded-full bg-red-500"></div>
                      <div className="h-3 w-3 rounded-full bg-yellow-500"></div>
                      <div className="h-3 w-3 rounded-full bg-green-500"></div>
                    </div>
                    <div className="text-xs text-gray-400 font-medium">
                      Restaurant Dashboard
                    </div>
                    <div className="w-6"></div>
                  </div>
                  <div className="bg-gray-900 border-2 border-gray-800 p-1">
                    {/* Added a container with fixed dimensions to improve mobile display */}
                    <div
                      className="relative overflow-hidden"
                      style={{ backgroundColor: "#111827" }}
                    >
                      {/* Added reflection overlay */}
                      <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent pointer-events-none z-10"></div>
                      <img
                        src="https://i.imgur.com/O80bYRH.png"
                        alt="ByteBelly Desktop Dashboard"
                        className="w-full h-auto"
                      />
                    </div>
                  </div>
                  <div className="bg-gradient-to-r from-gray-900 to-gray-800 h-3 rounded-b-lg"></div>
                  <div className="bg-gradient-to-r from-gray-700 to-gray-800 h-8 w-28 mx-auto rounded-b-xl"></div>
                  {/* Added shadow to create a more realistic look on mobile */}
                  <div className="absolute -bottom-4 left-0 right-0 h-4 bg-black/10 blur-md rounded-full mx-10"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Features Section */}
      <section className="section relative bg-zinc-50">
        {/* Background Texture */}
        <div
          className="absolute inset-0 z-0 opacity-[0.02]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 40 40'%3E%3Cg fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.4'%3E%3Cpath d='M0 38.59l2.83-2.83 1.41 1.41L1.41 40H0v-1.41zM0 20.83l2.83-2.83 1.41 1.41L1.41 22.24H0v-1.41zM0 3.07l2.83-2.83 1.41 1.41L1.41 4.48H0V3.07zm20.76-8.24l2.83-2.83 1.41 1.41L22.17 0H20.76v-5.17zm0 17.66l2.83-2.83 1.41 1.41-2.83 2.83H20.76v-1.41zm0 18.76l2.83-2.83 1.41 1.41-2.83 2.83H20.76V33.18zM18.34 0l2.83 2.83-1.41 1.41L16.93 1.41V0h1.41zm0 17.66l2.83 2.83-1.41 1.41-2.83-2.83v-1.41h1.41zm0 18.76l2.83 2.83-1.41 1.41-2.83-2.83v-1.41h1.41zM40 14.83V16.24l-2.83 2.83-1.41-1.41L38.59 14.83H40zM40 32.59V34l-2.83 2.83-1.41-1.41L38.59 32.59H40zM40 0v1.41l-2.83 2.83-1.41-1.41L38.59 0H40zm-21.66 0v-1.41l2.83-2.83 1.41 1.41L20.17 0h-1.41zm0 17.66v-1.41l2.83-2.83 1.41 1.41-2.83 2.83h-1.41zm0 18.76v-1.41l2.83-2.83 1.41 1.41-2.83 2.83h-1.41z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        ></div>

        <div className="container-custom relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              {t("allInOneTitle")}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t("allInOneDescription")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <motion.div whileHover={{ y: -5 }} className="feature-card">
              <div className="mb-4 bg-orange-100 inline-block p-3 rounded-lg">
                <FiSmartphone className="h-6 w-6 text-orange-500" />
              </div>
              <h3 className="text-xl font-bold mb-2">{t("mobileOrdering")}</h3>
              <p className="text-gray-600">{t("mobileOrderingDesc")}</p>
            </motion.div>

            {/* Feature 2 */}
            <motion.div whileHover={{ y: -5 }} className="feature-card">
              <div className="mb-4 bg-blue-100 inline-block p-3 rounded-lg">
                <FiShoppingBag className="h-6 w-6 text-blue-500" />
              </div>
              <h3 className="text-xl font-bold mb-2">{t("adminApp")}</h3>
              <p className="text-gray-600">{t("adminAppDesc")}</p>
            </motion.div>

            {/* Feature 3 */}
            <motion.div whileHover={{ y: -5 }} className="feature-card">
              <div className="mb-4 bg-green-100 inline-block p-3 rounded-lg">
                <FiTruck className="h-6 w-6 text-green-500" />
              </div>
              <h3 className="text-xl font-bold mb-2">{t("orderTracking")}</h3>
              <p className="text-gray-600">{t("orderTrackingDesc")}</p>
            </motion.div>

            {/* Feature 4 */}
            <motion.div whileHover={{ y: -5 }} className="feature-card">
              <div className="mb-4 bg-purple-100 inline-block p-3 rounded-lg">
                <FiTag className="h-6 w-6 text-purple-500" />
              </div>
              <h3 className="text-xl font-bold mb-2">{t("discountSystem")}</h3>
              <p className="text-gray-600">{t("discountSystemDesc")}</p>
            </motion.div>

            {/* Feature 5 */}
            <motion.div whileHover={{ y: -5 }} className="feature-card">
              <div className="mb-4 bg-yellow-100 inline-block p-3 rounded-lg">
                <FiMoon className="h-6 w-6 text-yellow-500" />
              </div>
              <h3 className="text-xl font-bold mb-2">{t("darkMode")}</h3>
              <p className="text-gray-600">{t("darkModeDesc")}</p>
            </motion.div>

            {/* Feature 6 */}
            <motion.div whileHover={{ y: -5 }} className="feature-card">
              <div className="mb-4 bg-red-100 inline-block p-3 rounded-lg">
                <FiGlobe className="h-6 w-6 text-red-500" />
              </div>
              <h3 className="text-xl font-bold mb-2">{t("multilanguage")}</h3>
              <p className="text-gray-600">{t("multilanguageDesc")}</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Demo Section */}
      <section className="section relative bg-zinc-100">
        {/* Background Texture */}
        <div
          className="absolute inset-0 z-0 opacity-[0.02]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='52' height='26' viewBox='0 0 52 26' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.4'%3E%3Cpath d='M10 10c0-2.21-1.79-4-4-4-3.314 0-6-2.686-6-6h2c0 2.21 1.79 4 4 4 3.314 0 6 2.686 6 6 0 2.21 1.79 4 4 4 3.314 0 6 2.686 6 6 0 2.21 1.79 4 4 4v2c-3.314 0-6-2.686-6-6 0-2.21-1.79-4-4-4-3.314 0-6-2.686-6-6zm25.464-1.95l8.486 8.486-1.414 1.414-8.486-8.486 1.414-1.414z' /%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        ></div>

        <div className="container-custom relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="text-center md:text-left"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                {t("tryLiveDemo")}
              </h2>
              <p className="text-lg text-gray-600 mb-6">{t("liveDemoDesc")}</p>
              <a
                href="https://foodapp.solylock.dev"
                target="_blank"
                rel="noreferrer"
                className="btn btn-primary inline-flex items-center whitespace-nowrap"
              >
                <span>{t("visitDemoSite")}</span>
                <svg
                  className="ml-2 w-4 h-4 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  ></path>
                </svg>
              </a>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="relative"
            >
              <div className="bg-zinc-300 rounded-xl p-4 shadow-md">
                <div className="aspect-w-16 aspect-h-9 rounded-lg overflow-hidden">
                  <img
                    src="https://i.imgur.com/3o5HOHk.png"
                    alt="ByteBelly Demo Screenshot"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section bg-white">
        <div className="container-custom">
          <div className="rounded-2xl bg-gradient-to-r from-orange-500 to-red-600 p-8 md:p-12 shadow-md text-white text-center">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              {t("transformRestaurant")}
            </h2>
            <p className="text-lg md:text-xl mb-8 max-w-2xl mx-auto">
              {t("joinRestaurants")}
            </p>
            <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
              <Link
                to="/pricing"
                className="bg-white text-orange-600 hover:bg-gray-100 py-3 px-8 rounded-lg font-medium shadow-sm inline-block"
              >
                {t("viewPricing")}
              </Link>
              <Link
                to="/contact"
                className="bg-transparent text-white border border-white hover:bg-white/10 py-3 px-8 rounded-lg font-medium shadow-sm inline-block"
              >
                {t("contactUs")}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* New section */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">
              {t("experienceOnAllDevices")}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t("responsiveDesignDescription")}
            </p>
          </div>

          {/* Desktop version - hidden on mobile */}
          <div className="hidden md:block relative max-w-5xl mx-auto h-[550px] sm:h-[600px] md:h-[650px] perspective-1000">
            {/* Desktop mockup */}
            <div className="absolute left-0 bottom-0 w-[65%] shadow-2xl rounded-xl overflow-hidden border-[10px] border-gray-800 bg-gradient-to-br from-gray-900 to-black transform -rotate-6 hover:rotate-3 transition-transform duration-700 z-10">
              <div className="bg-gradient-to-r from-gray-900 to-gray-800 h-7 flex items-center px-3">
                <div className="flex space-x-2">
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                </div>
                <div className="text-xs text-gray-400 ml-4">
                  ByteBelly Dashboard
                </div>
              </div>
              <div className="relative overflow-hidden">
                {/* Screen reflection */}
                <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent z-10 pointer-events-none"></div>
                <img
                  src="https://i.imgur.com/O80bYRH.png"
                  alt="ByteBelly Admin Dashboard"
                  className="w-full h-auto"
                />
              </div>
              {/* Desktop shadow/reflection */}
              <div className="absolute -bottom-12 left-0 right-0 h-12 bg-black/10 blur-md rounded-full mx-16"></div>
            </div>

            {/* First mobile mockup - Customer App */}
            <div className="absolute right-[18%] sm:right-16 top-0 w-[28%] sm:w-[26%] md:w-[24%] shadow-2xl rounded-[32px] overflow-hidden border-[10px] border-gray-800 bg-black transform rotate-3 hover:rotate-0 transition-transform duration-700 z-20">
              <div className="bg-black h-5 flex justify-center relative">
                <div className="absolute top-0 w-20 h-5 bg-black left-1/2 transform -translate-x-1/2 rounded-b-xl z-10"></div>
                <div className="w-3 h-3 bg-gray-800 rounded-full absolute right-2 top-1"></div>
              </div>

              {/* Power & volume buttons */}
              <div className="absolute -right-[10px] top-24 w-[10px] h-16 bg-gray-700 rounded-l-md"></div>
              <div className="absolute -left-[10px] top-24 w-[10px] h-10 bg-gray-700 rounded-r-md"></div>
              <div className="absolute -left-[10px] top-40 w-[10px] h-10 bg-gray-700 rounded-r-md"></div>

              <div className="relative">
                {/* Screen reflection */}
                <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-transparent to-white/10 pointer-events-none z-10"></div>
                <img
                  src="https://i.imgur.com/FsRbh4o.png"
                  alt="ByteBelly Customer App"
                  className="w-full h-auto"
                />
              </div>

              {/* Home indicator */}
              <div className="bg-black h-6 flex justify-center items-center">
                <div className="w-20 h-1 bg-gray-600 rounded-full"></div>
              </div>
            </div>

            {/* Second mobile mockup - Admin App */}
            <div className="absolute right-[4%] sm:right-0 top-40 w-[28%] sm:w-[26%] md:w-[24%] shadow-2xl rounded-[32px] overflow-hidden border-[10px] border-gray-800 bg-black transform -rotate-6 hover:rotate-2 transition-transform duration-700 z-30">
              <div className="bg-black h-5 flex justify-center relative">
                <div className="absolute top-0 w-20 h-5 bg-black left-1/2 transform -translate-x-1/2 rounded-b-xl z-10"></div>
                <div className="w-3 h-3 bg-gray-800 rounded-full absolute right-2 top-1"></div>
              </div>

              {/* Power & volume buttons */}
              <div className="absolute -right-[10px] top-24 w-[10px] h-16 bg-gray-700 rounded-l-md"></div>
              <div className="absolute -left-[10px] top-24 w-[10px] h-10 bg-gray-700 rounded-r-md"></div>
              <div className="absolute -left-[10px] top-40 w-[10px] h-10 bg-gray-700 rounded-r-md"></div>

              <div className="relative">
                {/* Screen reflection */}
                <div className="absolute inset-0 bg-gradient-to-tl from-transparent via-transparent to-white/10 pointer-events-none z-10"></div>
                <img
                  src="https://i.imgur.com/QT60F5t.png"
                  alt="ByteBelly Admin App"
                  className="w-full h-auto"
                />
              </div>

              {/* Home indicator */}
              <div className="bg-black h-6 flex justify-center items-center">
                <div className="w-20 h-1 bg-gray-600 rounded-full"></div>
              </div>
            </div>

            {/* Decorative elements with blur and glow effects */}
            <div className="absolute -left-10 top-20 w-32 h-32 bg-orange-500 opacity-20 rounded-full blur-xl"></div>
            <div className="absolute right-40 bottom-10 w-40 h-40 bg-blue-500 opacity-10 rounded-full blur-xl"></div>
            <div className="absolute left-60 top-10 w-24 h-24 bg-green-500 opacity-15 rounded-full blur-xl"></div>
            <div className="absolute right-20 top-40 w-16 h-16 bg-purple-500 opacity-10 rounded-full blur-lg"></div>
          </div>

          {/* Mobile version - much more appealing presentation */}
          <div className="md:hidden">
            <div className="flex flex-col items-center justify-center space-y-8">
              {/* Desktop Dashboard */}
              <div className="w-full max-w-md mx-auto transform transition-all duration-500">
                <div className="relative mx-auto">
                  {/* Monitor Frame */}
                  <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-t-xl p-2 flex items-center justify-between">
                    <div className="flex space-x-1.5">
                      <div className="h-3 w-3 rounded-full bg-red-500"></div>
                      <div className="h-3 w-3 rounded-full bg-yellow-500"></div>
                      <div className="h-3 w-3 rounded-full bg-green-500"></div>
                    </div>
                    <div className="text-xs text-gray-400 font-medium">
                      Restaurant Dashboard
                    </div>
                    <div className="w-6"></div>
                  </div>
                  <div className="bg-gray-900 border-2 border-gray-800 p-2">
                    <div className="relative overflow-hidden bg-gray-900">
                      {/* Added reflection overlay */}
                      <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent pointer-events-none z-10"></div>
                      <img
                        src="https://i.imgur.com/O80bYRH.png"
                        alt="ByteBelly Desktop Dashboard"
                        className="w-full h-auto"
                      />
                    </div>
                  </div>
                  <div className="bg-gradient-to-r from-gray-900 to-gray-800 h-3 rounded-b-lg"></div>
                  <div className="bg-gradient-to-r from-gray-700 to-gray-800 h-8 w-28 mx-auto rounded-b-xl"></div>
                  {/* Added shadow to create a more realistic look */}
                  <div className="absolute -bottom-4 left-0 right-0 h-4 bg-black/10 blur-md rounded-full mx-10"></div>
                </div>
              </div>

              {/* Mobile Apps Side by Side - much larger and more appealing */}
              <div className="flex justify-center items-start gap-8 w-full py-6">
                {/* Admin Mobile App */}
                <div className="transform transition-all duration-500 hover:scale-105 w-[40%] max-w-[160px]">
                  <div className="relative bg-black rounded-[30px] border-[8px] border-gray-800 shadow-xl overflow-hidden">
                    {/* Notch */}
                    <div className="absolute top-0 w-1/2 h-5 bg-black left-1/2 transform -translate-x-1/2 rounded-b-xl z-10"></div>

                    {/* Screen */}
                    <div className="pt-6 pb-3 flex items-center justify-center bg-gradient-to-b from-gray-900 to-black">
                      <img
                        src="https://i.imgur.com/QT60F5t.png"
                        alt="ByteBelly Admin App"
                        className="w-full object-contain"
                      />
                    </div>

                    {/* Home indicator */}
                    <div className="h-6 flex justify-center items-center bg-black">
                      <div className="w-16 h-1 bg-gray-600 rounded-full"></div>
                    </div>
                  </div>
                  <div className="mt-2 text-center">
                    <div className="bg-gradient-to-r from-blue-600 to-blue-500 text-white text-xs px-4 py-1.5 rounded-full inline-block shadow-lg">
                      Admin
                    </div>
                  </div>
                </div>

                {/* Customer Mobile App */}
                <div className="transform transition-all duration-500 hover:scale-105 w-[40%] max-w-[160px]">
                  <div className="relative bg-black rounded-[30px] border-[8px] border-gray-800 shadow-xl overflow-hidden">
                    {/* Notch */}
                    <div className="absolute top-0 w-1/2 h-5 bg-black left-1/2 transform -translate-x-1/2 rounded-b-xl z-10"></div>

                    {/* Screen */}
                    <div className="pt-6 pb-3 flex items-center justify-center bg-gradient-to-b from-gray-900 to-black">
                      <img
                        src="https://i.imgur.com/FsRbh4o.png"
                        alt="ByteBelly Customer App"
                        className="w-full object-contain"
                      />
                    </div>

                    {/* Home indicator */}
                    <div className="h-6 flex justify-center items-center bg-black">
                      <div className="w-16 h-1 bg-gray-600 rounded-full"></div>
                    </div>
                  </div>
                  <div className="mt-2 text-center">
                    <div className="bg-gradient-to-r from-orange-600 to-orange-500 text-white text-xs px-4 py-1.5 rounded-full inline-block shadow-lg">
                      Customer
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-16 text-center">
            <p className="text-lg text-gray-700 mb-6">
              {t("availableOnAllPlatforms")}
            </p>
            <div className="flex flex-wrap justify-center">
              <button className="flex items-center bg-gray-900 text-white px-5 py-3 rounded-lg hover:bg-gray-800 transition">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 mr-2"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M17.5 12.5c0-1.12-0.32-2.12-0.95-3-0.63-0.88-1.5-1.41-2.55-1.56v-0.02c0.97-0.49 1.5-1.34 1.5-2.56 0-0.83-0.29-1.5-0.87-2.03-0.58-0.53-1.34-0.79-2.26-0.79h-6.74v12h7.39c0.88 0 1.63-0.29 2.26-0.88 0.63-0.58 0.95-1.31 0.95-2.16zM8.95 4.58h3.18c0.4 0 0.72 0.1 0.96 0.31 0.24 0.2 0.36 0.48 0.36 0.85 0 0.37-0.12 0.65-0.36 0.85-0.24 0.2-0.56 0.31-0.96 0.31h-3.18v-2.32zM13.52 13c-0.28 0.24-0.65 0.37-1.1 0.37h-3.47v-2.91h3.47c0.45 0 0.81 0.12 1.1 0.37 0.28 0.24 0.43 0.58 0.43 1.01s-0.15 0.92-0.43 1.16z"></path>
                </svg>
                ByteBelly
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
